/**
 * SectionNav -- 좌측 2fr 섹션 목록 (아이콘 + 라벨)
 *
 * 기본 정보 / 콘텐츠 / 추가 기능 / 설정 네 그룹으로 나뉘며, 활성 섹션을 하이라이트로 표시.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  MessageSquare, Users, Calendar, Image, Phone,
  CreditCard, UserCheck, BookOpen, Heart, Clock, Info,
  Type, Music, Share2, Settings,
  ChevronUp, ChevronDown,
} from 'lucide-react'
import { SECTION_LABELS, type SectionId } from '@cugumi/shared/schemas'
import { cn } from '../../lib/cn.js'
import {
  type AllSectionId,
  type SettingSectionId,
  SETTING_LABELS,
  SETTING_IDS,
  TOGGLEABLE_SECTIONS,
  CONTENT_SECTION_GROUPS,
} from './types.js'
import styles from './SectionNav.module.css'

const CONTENT_ICONS: Partial<Record<SectionId, ReactNode>> = {
  greeting: <MessageSquare size={20} />,
  parents: <Users size={20} />,
  ceremony: <Calendar size={20} />,
  gallery: <Image size={20} />,
  contacts: <Phone size={20} />,
  accounts: <CreditCard size={20} />,
  rsvp: <UserCheck size={20} />,
  guestbook: <BookOpen size={20} />,
  timeline: <Heart size={20} />,
  schedule: <Clock size={20} />,
  notices: <Info size={20} />,
}

const SETTING_ICONS: Record<SettingSectionId, ReactNode> = {
  fontStyle: <Type size={20} />,
  bgm: <Music size={20} />,
  kakaoShare: <Share2 size={20} />,
  settings: <Settings size={20} />,
}

interface SectionNavProps {
  activeSection: AllSectionId
  onSelect: (id: AllSectionId) => void
  sectionOrder: SectionId[]
  enabledSections?: Record<string, boolean>
}

export function SectionNav({ activeSection, onSelect, sectionOrder, enabledSections = {} }: SectionNavProps) {
  const navRef = useRef<HTMLElement>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  useEffect(() => {
    const el = navRef.current
    if (!el) return

    const check = () => {
      setCanScrollUp(el.scrollTop > 4)
      setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 4)
    }

    check()
    el.addEventListener('scroll', check, { passive: true })
    const ro = new ResizeObserver(check)
    ro.observe(el)

    return () => {
      el.removeEventListener('scroll', check)
      ro.disconnect()
    }
  }, [])

  return (
    <div className={styles.wrapper}>
      <div className={cn(styles.scrollHint, canScrollUp && styles.visible)}>
        <button type="button" className={styles.scrollHintBtn} aria-label="위로 스크롤"
          onClick={() => navRef.current?.scrollBy({ top: -200, behavior: 'smooth' })}>
          <ChevronUp size={14} />
        </button>
      </div>
      <nav ref={navRef} className={styles.root} aria-label="섹션 네비게이터">
        {CONTENT_SECTION_GROUPS.map((group) => {
          const groupSections = group.sections.filter((s) => sectionOrder.includes(s))
          if (groupSections.length === 0) return null
          return (
            <div key={group.label} className={styles.group}>
              <div className={styles.groupHeader}>{group.label}</div>
              {groupSections.map((id) => {
                const toggleKey = TOGGLEABLE_SECTIONS[id]
                const isHidden = toggleKey ? enabledSections[toggleKey] === false : false
                return (
                  <button
                    key={id}
                    type="button"
                    className={cn(
                      styles.item,
                      activeSection === id && styles.active,
                    )}
                    onClick={() => onSelect(id)}
                    aria-current={activeSection === id ? 'true' : undefined}
                  >
                    <span className={styles.itemIcon}>
                      {CONTENT_ICONS[id]}
                      {toggleKey ? (
                        <span className={cn(styles.statusDot, isHidden && styles.statusDotHidden)} />
                      ) : (
                        <span className={styles.requiredBadge}>필수</span>
                      )}
                    </span>
                    <span className={styles.itemLabel}>{SECTION_LABELS[id]}</span>
                  </button>
                )
              })}
            </div>
          )
        })}

        <div className={styles.group}>
        <div className={styles.groupHeader}>설정</div>
        {SETTING_IDS.map((id) => (
          <button
            key={id}
            type="button"
            className={cn(styles.item, activeSection === id && styles.active)}
            onClick={() => onSelect(id)}
            aria-current={activeSection === id ? 'true' : undefined}
          >
            <span className={styles.itemIcon}>{SETTING_ICONS[id]}</span>
            <span className={styles.itemLabel}>{SETTING_LABELS[id]}</span>
          </button>
        ))}
        </div>
      </nav>
      <div className={cn(styles.scrollHint, canScrollDown && styles.visible)}>
        <button type="button" className={styles.scrollHintBtn} aria-label="아래로 스크롤"
          onClick={() => navRef.current?.scrollBy({ top: 200, behavior: 'smooth' })}>
          <ChevronDown size={14} />
        </button>
      </div>
    </div>
  )
}
